/* eslint-disable quotes */
import React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { HocPopupComponent } from 'Components/common/hocPopupComponent';
import Button from 'Components/common/Button';
import upgradeCommonImg from 'Assets/png/upgrade-common.png';
import { ReactComponent as TickIcon } from 'Assets/svg/tick-icon-new.svg';
import { ReactComponent as CloseIcon } from 'Assets/svg/close-circle.svg';
import 'Styles/popup/_commonUpgradePopup.scss';
import { Utils } from 'Utils/utils';
import { CONTACT_COUNT_IN_MILLIONS, MX_COMMON_EVENTS } from 'Utils/constants';

const UPGRADE_POPUP_CASES = {
  BULK_EXPORT_IN_ADV: {
    title: 'Increase your productivity with bulk actions.',
    message:
      "Upgrade to Adapt's paid plans and get access to advanced features.",
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'bulk_export',
  },
  COMMON_PLATFORM_FEATURE: {
    title: `Unlock our ${CONTACT_COUNT_IN_MILLIONS} database to find your target contacts instantly with Adapt’s premium plans.`,
    btnText: 'Upgrade Now',
    featureListMap: {
      free: true,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'common',
  },
  SINGLE_CSV_EXPORT: {
    title:
      'This is an advanced feature. Upgrade to any of the Adapt premium plans to get access.',
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'single_csv_export',
  },
  PAGINATION: {
    title: `Unlock our ${CONTACT_COUNT_IN_MILLIONS} database to find your target contacts instantly with Adapt’s premium plans.`,
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'pagination',
  },
  VIEW_CONTACT_NO_CREDIT: {
    title: 'Oops, not enough credits!',
    message:
      'Upgrade to one of our premium plans and get more credits. Our plans start at less than $10/month.',
    btnText: 'Upgrade Now',
    mxSource: 'no_credits',
  },
  EXPORT_SELECTED_TO_CSV_IN_MY_LEADS: {
    title: 'Upgrade to move your contacts into your workspace!',
    message:
      "The free plan doesn't support exporting leads to CSV. Upgrade now and join our community of 500K+ prospectors",
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'csv_export',
  },
  EXPORT_SELECTED_TO_CRM_IN_MY_LEADS: {
    title: 'Upgrade to move your contacts into your workspace!',
    message:
      "The free plan doesn't support exporting leads to CRM. Upgrade now and join our community of 500K+ prospectors",
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: false,
      growth: true,
      enterprise: true,
    },
  },
  EXPORT_LIST_IN_MY_LEADS: {
    title: 'Increase your productivity with list actions.',
    message: 'Upgrade to Adapt paid plans and get access to export your list.',
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'export_list',
  },
  CRM_INTEGRATIONS: {
    title: 'Upgrade to move your contacts into your workspace!',
    message:
      "The free plan doesn't support exporting leads. Upgrade now and join the community 500K+ prospectors",
    btnText: 'Upgrade Now',
  },
  STARTER_CRM_EXPORT: {
    title: 'STARTER plan does not support CRM integration',
    message:
      'Upgrade to higher plan and unlock additional features to supercharge your productivity',
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: false,
      growth: true,
      enterprise: true,
    },
    mxSource: 'crm_export',
  },
  PURCHASE_PHONE: {
    title: 'Upgrade to our premium plan to to access Mobile Numbers',
    message:
      'Our free plan does not include access to mobile numbers. Upgrade now for instant access',
    btnText: 'Upgrade Now',
    featureListMap: {
      free: false,
      starter: true,
      growth: true,
      enterprise: true,
    },
    mxSource: 'purchase_phone',
  },
};

class CommonUpgradePopupState {
  @observable showUpgradePopup = null;

  defaultPopupValue = {
    callback: null,
  };

  @observable popupValues = this.defaultPopupValue;

  @action
  setShowUpgradePopup = value => {
    this.showUpgradePopup = value;
  };

  @action
  setPopupValues = data => {
    this.popupValues = {
      callback: data.callback || null,
    };
  };
}

const commonUpgradePopupState = new CommonUpgradePopupState();

const CommonUpgradePopup = observer(() => {
  const { showUpgradePopup, popupValues } = commonUpgradePopupState;
  const { callback = null } = popupValues;

  const {
    className = '',
    title,
    message = null,
    btnText,
    featureListMap = {},
    mxSource,
  } = showUpgradePopup ? UPGRADE_POPUP_CASES[showUpgradePopup] : {};

  const closeClick = e => {
    commonUpgradePopupState.setShowUpgradePopup(null);
  };

  const actionCallback = e => {
    if (callback) {
      callback();
    } else {
      Utils.mixpanelTrack(MX_COMMON_EVENTS.LB_UPGRADE_CLICK, {
        Source: mxSource,
      });
      Utils.redirectToLBPricingPage();
      commonUpgradePopupState.setShowUpgradePopup(null);
    }
  };

  const featureList = Object.keys(featureListMap);
  return commonUpgradePopupState.showUpgradePopup !== null ? (
    <HocPopupComponent
      showCloseIcon
      customClassName={`${className} common-upgrade-popup`}
      closeClick={e => closeClick(e)}
    >
      <img
        src={upgradeCommonImg}
        className="upgrade-img"
        alt="upgradeCommonImg"
      />
      <div className="title">{title}</div>
      {message ? <div className="message">{message}</div> : ''}
      {featureList.length > 0 ? (
        <div className="feature-availability">
          <div className="feature-heading">Feature available in</div>
          <div className="feature-list">
            {featureList.map((item, index) => {
              return (
                <div className="feature-item" key={`${item}-${index}`}>
                  {featureListMap[item] ? (
                    <TickIcon className="svg-icon tick" />
                  ) : (
                    <CloseIcon className="svg-icon close" />
                  )}
                  {item}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        ''
      )}
      <div className="button-wrapper">
        {/* <div className="not-now-btn" onClick={e => closeClick(e)}>
          Not now
        </div> */}
        <Button
          className="upgrade-btn"
          btnText={btnText}
          click={e => actionCallback(e)}
        />
      </div>
    </HocPopupComponent>
  ) : null;
});

export { commonUpgradePopupState };
export default CommonUpgradePopup;
